import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './FreeTrial.css';

const CaptainBizLanding = () => {
  const appName = "GST Prime";
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: '📊',
      title: 'GST Invoicing',
      description: 'Create GST-ready invoices with automatic tax calculations. Industry-specific templates for both GST and non-GST companies.'
    },
    {
      icon: '📦',
      title: 'Inventory Management',
      description: 'Real-time inventory tracking with automatic reorder alerts. Bulk upload items and track stock levels efficiently.'
    },
    {
      icon: '💰',
      title: 'Payment Tracking',
      description: 'Track payments and outstanding dues. Get automatic reminders for overdue invoices and improve cash flow.'
    },
    {
      icon: '📱',
      title: 'Multi-Platform Access',
      description: 'Access from anywhere - mobile app (Android/iOS) or web browser. Sync your data across all devices.'
    },
    {
      icon: '📈',
      title: 'GST Reports',
      description: 'Generate GST-ready reports for tax filing. E-invoice capable and empanelled by GSTN India.'
    },
    {
      icon: '🔒',
      title: 'Secure & Compliant',
      description: 'Data hosted in India with SSL encryption. GSTN approved and compliant with all GST regulations.'
    }
  ];

  const benefits = [
    {
      icon: '💳',
      title: 'Track payments',
      items: [
        'GST approved UOMs supported',
        'Industry specific invoice templates',
        'Share invoices over WhatsApp or Email'
      ]
    },
    {
      icon: '📦',
      title: 'Inventory Management',
      items: [
        'Automatic Sales/Purchase updates',
        'Real-time check for efficient reorder',
        'Bulk upload of items'
      ]
    },
    {
      icon: '📊',
      title: 'GST Reports',
      items: [
        'Generate GST-ready reports in one click',
        'E-invoice capable',
        'Empanelled by GSTN India'
      ]
    }
  ];

  const handleStartTrial = () => {
    navigate('/login');
  };

  const handleWatchDemo = () => {
    alert('Demo video would open here!');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="captainbiz-app">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <span className="logo-icon">⚡</span>
            <h1>{appName}</h1>
          </div>
          <nav className="nav">
            <a href="#home">Home</a>
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#support">Blogs</a>
            <a href="#support">Resources</a>
            <Link to="/login" className="cta-button-light" style={{ textDecoration: 'none', color: 'white' }}>Sign Up</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h2>GST Billing & Inventory Software made for Indian MSMEs</h2>
              <p>Get your invoicing organized, get paid on time, control your inventory, reduce costs and grow business smoothly.</p>
              <div className="hero-buttons">
                <button className="btn btn-primary btn-large" onClick={handleStartTrial}>
                  Start 31 Day Free Trial
                </button>
                <button className="btn btn-outline btn-large" onClick={handleWatchDemo}>
                  Watch Demo
                </button>
              </div>
              <p className="trial-note">No payment required • GST Approved • GSTN Empanelled</p>
            </div>
            
            <div className="hero-visual">
              <div className="software-preview">
                <div className="screen">
                  <div className="screen-header">
                    <div className="screen-buttons">
                      <span className="btn-circle red"></span>
                      <span className="btn-circle yellow"></span>
                      <span className="btn-circle green"></span>
                    </div>
                  </div>
                  <div className="screen-content">
                    <div className="invoice-form">
                      <h3>Create Invoice</h3>
                      <div className="form-row">
                        <input type="text" placeholder="Invoice No: INV-001" />
                        <input type="date" />
                      </div>
                      <div className="form-row">
                        <input type="text" placeholder="Customer Name" />
                        <input type="text" placeholder="GSTIN" />
                      </div>
                      <div className="items-table">
                        <div className="table-header">
                          <span>Item</span>
                          <span>Qty</span>
                          <span>Rate</span>
                          <span>Amount</span>
                        </div>
                        <div className="table-row">
                          <span>Product A</span>
                          <span>2</span>
                          <span>₹500</span>
                          <span>₹1000</span>
                        </div>
                      </div>
                      <div className="total-section">
                        <div className="total-row">Subtotal: ₹1000</div>
                        <div className="total-row">CGST (9%): ₹90</div>
                        <div className="total-row">SGST (9%): ₹90</div>
                        <div className="total-row total-final">Total: ₹1180</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <h2 className="section-title">Everything you need to manage your business</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div 
                key={index}
                id={`feature-${index}`}
                data-animate
                className={`feature-card ${isVisible[`feature-${index}`] ? 'visible' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                id={`benefit-${index}`}
                data-animate
                className={`benefit-item ${index % 2 === 1 ? 'benefit-reverse' : ''} ${isVisible[`benefit-${index}`] ? 'visible' : ''}`}
              >
                <div className="benefit-visual">
                  <div className="benefit-icon">{benefit.icon}</div>
                </div>
                <div className="benefit-text">
                  <h3>{benefit.title}</h3>
                  <ul>
                    {benefit.items.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial">
        <div className="container">
          <div className="testimonial-content">
            <div className="testimonial-text">
              <blockquote>
                "I'm not a tech-savvy but I set up my billing in minutes."
              </blockquote>
              <cite>- Radhika Sharma, Jaipur, Rajasthan</cite>
            </div>
            <div className="testimonial-stats">
              <div className="stat">
                <div className="stat-number">50,000+</div>
                <div className="stat-label">Happy Customers</div>
              </div>
              <div className="stat">
                <div className="stat-number">99.9%</div>
                <div className="stat-label">Uptime</div>
              </div>
              <div className="stat">
                <div className="stat-number">24/7</div>
                <div className="stat-label">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to streamline your business?</h2>
            <p>Join thousands of businesses already using CaptainBiz to manage their invoicing and inventory.</p>
            <div className="cta-buttons">
              <button className="btn btn-primary btn-large" onClick={handleStartTrial}>
                Start Free Trial
              </button>
              <button className="btn btn-outline btn-large">Schedule Demo</button>
            </div>
            <p className="cta-note">31-day free trial • No credit card required • Setup in minutes</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>CaptainBiz</h3>
              <p>GST Billing & Inventory Software made for Indian MSMEs</p>
            </div>
            <div className="footer-section">
              <h4>Product</h4>
              <ul>
                <li><a href="#">Features</a></li>
                <li><a href="#">Pricing</a></li>
                <li><a href="#">Integrations</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Support</h4>
              <ul>
                <li><a href="#">Help Center</a></li>
                <li><a href="#">Contact Us</a></li>
                <li><a href="#">Training</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li><a href="#">About</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Careers</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 CaptainBiz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaptainBizLanding;
